export default {
    title: 'Projects',
    subtitle: '{n} project | {n} projects',
    newProject: 'New project',
    untitled: 'Untitled project',
    titlePlaceholder: 'Project name, e.g. Bathroom renovation',
    titleLabel: 'Project name',
    progress: '{done} of {total}',
    allDone: 'All done — finish the project?',
    addTask: 'Add a task',
    addTaskTo: 'Add a task to “{name}”',
    done: 'Done ({n})',
    empty: {
        title: 'No projects yet',
        text: 'A project is something with a goal and an end — a renovation, a move, a trip — broken into tasks.',
    },
    pick: {
        title: 'Pick a project',
        text: 'Or start a new one — from scratch or from a template.',
    },
    finished: {
        title: 'Finished',
        back: 'Back to projects',
        open: 'Finished ({n})',
        empty: 'No finished projects yet',
    },
    due: {
        today: 'due today',
        tomorrow: 'due tomorrow',
        in: 'in {n} day | in {n} days',
        overdue: 'overdue ({n} day) | overdue ({n} days)',
    },
    day: {
        today: 'today',
        tomorrow: 'tomorrow',
        yesterday: 'yesterday',
    },
    toolbar: {
        back: 'Back to projects',
        due: 'Project deadline',
        addDue: 'Deadline',
        dueOn: '{date} · {when}',
        clearDue: 'Clear deadline',
        finishedOn: 'Finished {date}',
        pin: 'Pin',
        unpin: 'Unpin',
        finish: 'Finish project',
        restore: 'Restore',
        more: 'More',
        saveTemplate: 'Save as template',
        remove: 'Delete project',
    },
    section: {
        new: 'New section',
        untitled: 'Section',
        name: 'Section name',
        add: 'Add section',
        collapse: 'Collapse section',
        expand: 'Expand section',
        remove: 'Delete section “{name}” (tasks stay)',
    },
    task: {
        text: 'Task text',
        drag: 'Drag to move',
        when: 'When',
        remove: 'Delete task',
        today: 'Today',
        tomorrow: 'Tomorrow',
        day: 'Day',
        noDay: 'No day',
        time: 'Time',
        timeNeedsDay: 'A time needs a day.',
        more: 'Repeat and reminder…',
    },
    templates: {
        title: 'Templates',
        subtitle: '{n} template | {n} templates',
        newTemplate: 'New template',
        newName: 'New template',
        untitled: 'Untitled template',
        intro: 'Ready sets of tasks — one click turns a template into a project.',
        from: 'From template',
        manage: 'Manage templates',
        empty: 'No templates yet. Create them in the “Templates” tab or save a project as a template (“…” menu).',
        emptyTitle: 'No templates yet',
        emptyText: 'A template is a task list you repeat: packing for a trip, moving house, cleaning before the holidays. Create one here or save a project as a template.',
        count: '{n} task | {n} tasks',
        sections: '{n} section | {n} sections',
        remove: 'Delete template “{name}”',
        back: 'Back to templates',
        name: 'Template name',
        namePlaceholder: 'Template name, e.g. Packing for a trip',
        editHint: 'Changes save themselves. Projects already made from the template don’t change.',
        use: 'Create project',
        saved: 'Saved',
        saving: 'Saving…',
        saveError: 'Couldn’t save — we’ll try again with your next change',
        pickTitle: 'Pick a template',
        pickText: 'Or create a new one. You can also make a template from a project — “Save as template” in the project’s “…” menu.',
    },
    help: {
        list: {
            title: 'Projects',
            text: 'A project is something with a goal and an end — a renovation, a move, a trip — broken into tasks. Each shows its progress and deadline.',
            more: 'Pinned ones are on top. Finished projects go to “Finished” at the bottom of the list and can be restored.',
        },
        project: {
            title: 'A project',
            text: 'Change the name and tasks in place — it saves itself. On the bar on top: deadline, pin, finish; under “…” save as template and delete.',
            more: 'The project deadline shows in the Planner on its day. When everything is ticked off, the project offers to finish.',
        },
        tasks: {
            title: 'Tasks and sections',
            text: 'Type a task in “Add a task” and press Enter — add one after another or paste several lines at once. Sections (e.g. Kitchen, Bathroom) group tasks.',
            more: 'Drag tasks between sections by the handle. The calendar icon sets a day and time — then the task shows in the Planner; repeat and reminder are in the full task window. Done tasks move to “Done” at the bottom.',
        },
        fromTemplate: {
            title: 'From template',
            text: 'A new project from a ready set of tasks, e.g. “Packing for a trip”.',
            more: 'Create and edit templates in the “Templates” tab — or save a project as a template in its “…” menu.',
        },
        templates: {
            title: 'Templates',
            text: 'Task lists you repeat. Edit them in place; “Create project” turns a template into a new project.',
            more: 'A template keeps its sections. Changing a template doesn’t touch projects already made from it.',
        },
    },
    tour: {
        welcome: {
            title: 'Welcome to Projects',
            description: 'Things with a goal and an end — a renovation, a move, a trip — broken into tasks. A task with a day also goes to the Planner.',
        },
        project: {
            title: 'Inside a project',
            description: 'Open a project from the list: add tasks at the bottom of a group and tick them off, sections group tasks, and the calendar icon next to a task sets its day and time. On top: deadline, pin and “Finish project”.',
        },
        end: {
            title: 'That’s it',
            description: 'The guide is always under the “Help” button — First steps are there too. And “What’s this?” next to a section explains it without the guide.',
        },
    },
    firstSteps: {
        project: {
            title: 'First project',
            action: 'New project',
            what: 'Create a project, e.g. “Bathroom renovation”, and type a few tasks.',
            why: 'Everything to do in one place — with progress on a bar.',
        },
        section: {
            title: 'A section in a project',
            action: 'Add a section',
            what: 'Split the project into stages or places, e.g. Kitchen and Bathroom.',
            why: 'In a bigger project you see at once what’s left in which part.',
        },
        day: {
            title: 'A task with a day',
            action: 'Set a task’s day',
            what: 'Next to a task click the calendar icon and pick a day (and time).',
            why: 'The task shows in the Planner that day, with the project name.',
        },
        due: {
            title: 'Project deadline',
            action: 'Set a deadline',
            what: 'On the project bar click “Deadline” and pick a day.',
            why: 'The list shows how much time is left, and the Planner shows the deadline on its day.',
        },
        template: {
            title: 'A template',
            action: 'Create a template',
            what: 'Make a template of a list you repeat — here or from a project with “Save as template”.',
            why: 'Next time the project is one click away.',
        },
    },
    toast: {
        undo: 'Undo',
        removed: 'Project deleted',
        finished: 'Project finished',
        restored: 'Project restored',
        taskRemoved: 'Task deleted',
        sectionRemoved: 'Section “{name}” deleted — its tasks stayed',
        templateSaved: 'Template “{name}” saved',
        templateRemoved: 'Template “{name}” deleted',
    },
    errors: {
        addTask: 'Couldn’t add the task.',
        order: 'Couldn’t save the order.',
        generate: 'Couldn’t create a project from the template.',
    },
};
